import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  cableLockEventStatus,
  type default as CableLockEventStatus,
} from './CableLockEventStatus.js'
import { orderId, type OrderId } from './Order.js'

/** Identifier type for public.cable_lock_event_ */
export type CableLockEventId = string & { __brand: 'CableLockEventId' }

/** Represents the table public.cable_lock_event_ */
export default interface CableLockEventTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    CableLockEventId,
    CableLockEventId | undefined,
    CableLockEventId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.uuid */
  orderId: ColumnType<OrderId, OrderId, OrderId>

  /** Database type: public.cable_lock_event_status_ */
  status: ColumnType<
    CableLockEventStatus,
    CableLockEventStatus | undefined,
    CableLockEventStatus
  >

  /** Database type: pg_catalog.text */
  errorMessage: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.jsonb */
  serialsCreated: ColumnType<unknown, unknown | undefined, unknown>

  /** Database type: pg_catalog.timestamptz */
  processedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >
}

export type CableLockEvent = Selectable<CableLockEventTable>

export type CableLockEventInitializer = Insertable<CableLockEventTable>

export type CableLockEventMutator = Updateable<CableLockEventTable>

export const cableLockEventId =
  z.string() as unknown as z.Schema<CableLockEventId>

export const cableLockEvent = z.object({
  id: cableLockEventId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
  orderId: orderId,
  status: cableLockEventStatus,
  errorMessage: z.string().nullable().optional(),
  serialsCreated: z.unknown(),
  processedAt: z.coerce.date().nullable().optional(),
}) as unknown as z.Schema<CableLockEvent>

export const cableLockEventInitializer = z.object({
  id: cableLockEventId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  orderId: orderId,
  status: cableLockEventStatus.optional(),
  errorMessage: z.string().optional().nullable(),
  serialsCreated: z.unknown().optional(),
  processedAt: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<CableLockEventInitializer>

export const cableLockEventMutator = z.object({
  id: cableLockEventId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  orderId: orderId.optional(),
  status: cableLockEventStatus.optional(),
  errorMessage: z.string().optional().nullable(),
  serialsCreated: z.unknown().optional(),
  processedAt: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<CableLockEventMutator>
